import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener } from '@angular/core';
import {
    ActivatedRoute,
    NavigationCancel,
    NavigationError,
    RouteConfigLoadEnd,
    RouteConfigLoadStart,
    Router,
} from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { NotificationsManagementService } from 'api/notifications.api';
import { interval, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { LocaleService } from 'shared/locale/locale.service';
import { AuthService } from './providers/auth.service';
import { GoogleAnalyticsService } from './google-analytics.service';
import { CookieBannerComponent } from 'cookie-banner/cookie-banner.component';
import { OrganizationInfoService } from 'shared/organization-info/organization-info.service';
import { Title } from '@angular/platform-browser';
import { EnvironmentService } from 'providers/environment.service';
import { environment } from '../environments/environment';
import { GdprPrivacyService } from './shared/gdpr-privacy/gdpr-privacy.service';
import { CustomerPrivacyService } from './layout/customer-privacy/customer-privacy.service';
import { PopupService } from 'shared/popup-service/popup.service';
import { LicenseRedirectService } from 'providers/license-redirect.service';
import { FeatureFlagService } from './services/feature-flags';

@Component({
    selector: 'ce-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
@UntilDestroy()
export class AppComponent {
    /**
     * Flag is true when loading a lazy-loaded module.
     * Used to display a meaningful loading-screen when navigating to a new page,
     * making the application feel more responsive.
     * Navigating to a dialog for example the first time in thise session.
     */
    showRouteLoading = true;
    showLayout = false;

    constructor(
        private auth: AuthService,
        private translate: TranslateService,
        private notificationsManagement: NotificationsManagementService,
        private localeService: LocaleService,
        private router: Router,
        private cdRef: ChangeDetectorRef,
        private route: ActivatedRoute,
        private googleAnalyticsService: GoogleAnalyticsService,
        private cookieBannerComponent: CookieBannerComponent,
        private organizationInfoService: OrganizationInfoService,
        private titleService: Title,
        private environmentService: EnvironmentService,
        private gdprPrivacyService: GdprPrivacyService,
        private customerPrivacyService: CustomerPrivacyService,
        private readonly popupService: PopupService,
        private readonly licenseRedirectService: LicenseRedirectService,
        private featureFlagService: FeatureFlagService
    ) {
        // Show "loading screen" for when loading lazy-loaded routes and it takes a long time
        let asyncLoadCount = 0; // see https://www.bennadel.com/blog/3505-showing-a-loading-indicator-for-lazy-loaded-route-modules-in-angular-6-1-7.htm
        this.router.events
            .pipe(
                filter(
                    (ev) =>
                        ev instanceof RouteConfigLoadStart ||
                        ev instanceof RouteConfigLoadEnd ||
                        ev instanceof NavigationError ||
                        ev instanceof NavigationCancel
                ),
                map((ev) => ev instanceof RouteConfigLoadStart),
                map((loading) => (asyncLoadCount += loading ? 1 : -1)),
                untilDestroyed(this)
            )
            .subscribe((asyncLoadCount) => {
                if (asyncLoadCount > 0) {
                    this.showRouteLoading = true;
                } else {
                    this.showRouteLoading = false;
                }
                this.cdRef.markForCheck();
            });

        this.router.events
            .pipe(
                filter((ev) => ev instanceof RouteConfigLoadEnd),
                switchMap(() => this.route.firstChild?.data || of(null)),
                untilDestroyed(this)
            )
            .subscribe((data) => {
                this.showLayout = data?.hideLayout !== true;
                this.cdRef.markForCheck();
            });
    }

    @HostListener('document:keypress', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent): void {
        if (environment.production === false && event.key.toLowerCase() === 't' && event.ctrlKey && event.shiftKey) {
            this.featureFlagService.toggleFlag('enableDarkMode');
        } else if (event.key.toLowerCase() === 'd' && event.ctrlKey && event.shiftKey) {
            this.router.navigate(['/feature-flags']);
        } else if (event.key.toLowerCase() === 'g' && event.ctrlKey && event.shiftKey) {
            this.gdprPrivacyService.enabled$.pipe(take(1), untilDestroyed(this)).subscribe((enabled) => {
                this.popupService.neutral(`GDPR privacy filter: ${enabled ? 'off' : 'on'}`);
            });
            this.gdprPrivacyService.toggle();
        } else if (event.key.toLowerCase() === 'c' && event.ctrlKey && event.shiftKey) {
            this.customerPrivacyService.enabled$.pipe(take(1), untilDestroyed(this)).subscribe((enabled) => {
                this.popupService.neutral(`Customer privacy filter: ${enabled ? 'off' : 'on'}`);
            });
            this.customerPrivacyService.toggle();
        }
    }

    ngOnInit() {
        // TODO: Checking from the path if the user has selected a language
        // if localhost/dev, check basehref  instead
        this.localeService.setLocale();

        this.environmentService
            .getAppOrigin$()
            .pipe(untilDestroyed(this))
            .subscribe((appOrigin) => {
                this.titleService.setTitle(appOrigin === 'mykempower' ? 'MyKempower' : 'ChargEye');
            });
        // login stuff
        this.auth.ensureLoggedIn();
        // login event fired for GA4
        this.cookieBannerComponent.setLoginFired();

        // if usage analytics cookies not placed, set cookie banner to status 'not shown', otherwise set cookie banner as 'shown'
        if (this.cookieBannerComponent.readConsentChoice() === undefined) {
            this.cookieBannerComponent.setShowBanner(false);
        } else {
            this.cookieBannerComponent.setShowBanner(true);
            this.cookieBannerComponent.disableWrongEnvCookie();
        }

        // send user property data to Google Analytics
        this.googleAnalyticsService.getDataForGA();

        // Subscribe to dark mode feature flag
        this.featureFlagService
            .getFlagState$()
            .pipe(untilDestroyed(this))
            .subscribe((flags) => {
                const isDarkModeEnabled = flags['enableDarkMode'];
                if (isDarkModeEnabled) {
                    document.documentElement.classList.add('dark');
                } else {
                    document.documentElement.classList.remove('dark');
                }
            });

        interval(60 * 1000).subscribe(() => {
            this.auth.checkJwtExpiry().catch((error) => {
                console.error('JWT expiry check failed:', error);
            });
        });

        // this language will be used as a fallback when a translation isn't found in the current language
        this.translate.setDefaultLang('en');

        // the lang to use, if the lang isn't available, it will use the current loader to get them
        this.translate.use('en');

        // once logged in, connect
        this.auth.authenticated$
            .pipe(
                map((authenticated) => authenticated),
                distinctUntilChanged(),
                filter((auth) => auth)
            )
            .subscribe(() => {
                this.notificationsManagement.connect();

                // Check for license redirects on app startup (existing sessions)
                if (!globalThis.location.pathname.includes('/auth-callback')) {
                    this.licenseRedirectService.checkAndRedirectBasedOnLicense().catch((error) => {
                        console.error('License redirection check failed:', error);
                    });
                }
            });

        // Reconnect when organizationInfo changes (impersonation)
        // New connection will be properly scoped to the new tenant
        this.organizationInfoService.organizationInfo$.pipe(untilDestroyed(this)).subscribe((orgInfo) => {
            const currentTenantId = orgInfo?.impersonated?.tenantId || orgInfo?.originalTenantId;

            if (
                this.notificationsManagement.connectedTenantId &&
                this.notificationsManagement.connectedTenantId !== currentTenantId
            ) {
                this.notificationsManagement.disconnect();
                this.notificationsManagement.connect();
            }
        });
    }
}
